import React, { useEffect, useState, useRef } from "react";
import Navbar from "../components/Navbar";
import Footer from "../Footer";
import { Link, useNavigate } from "react-router-dom";
import Papa from "papaparse";
import axios from "axios";
import { Bounce, ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";

const BulkUploadCars = () => {
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [columns, setColumns] = useState([]);
  const [fileName, setFileName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const fileInputRef = useRef(null);

  const optionToast = {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "colored",
    transition: Bounce,
  };

  const getDataFromlocalStorage = (key) => {
    try {
      const serializedData = localStorage.getItem(key);
      return serializedData ? JSON.parse(serializedData) : null;
    } catch (error) {
      console.error("Error getting data from localStorage:", error);
      return null;
    }
  };

  const storedUserPreferences = getDataFromlocalStorage("userData");

  useEffect(() => {
    if (storedUserPreferences === null) {
      toast.warning("You need to be logged in as dealer", optionToast);
      navigate("/login");
      return;
    } else if (storedUserPreferences?.role !== "dealer") {
      toast.warning("Only dealer can access this page", optionToast);
      navigate("/");
      return;
    }
  }, []);

  const placeholderImage = "https://placehold.co/600x400?text=Car+Image";

  const handleDownloadSampleCsv = () => {
    const headers = [
      "registration_number",
      "fixed_price",
      "make",
      "model",
      "model_varient",
      "year",
      "postcode",
      "fuel_type",
      "body_type",
      "transmission_type",
      "color",
      "seating_capacity",
      "current_mileage",
      "engine_capacity",
      "specifications",
    ];

    const sampleRow = [
      "AB12CDE",
      "8995",
      "Ford",
      "Fiesta",
      "1.0 EcoBoost Titanium",
      "2019",
      "SW1A 1AA",
      "Petrol",
      "Hatchback",
      "Manual",
      "Blue",
      "5",
      "32000",
      "999",
      "Nice clean car, full service history.",
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return "";
      const stringValue = String(value);
      if (stringValue.includes(",") || stringValue.includes("\"")) {
        return '"' + stringValue.replace(/"/g, '""') + '"';
      }
      return stringValue;
    };

    const csvContent =
      headers.join(",") + "\n" + sampleRow.map(escapeCsv).join(",");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sample_cars.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a valid CSV file", optionToast);
      return;
    }

    setFileName(file.name);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.errors && result.errors.length > 0) {
          console.error(result.errors);
          toast.error("Error parsing CSV. Please check the file.", optionToast);
          return;
        }

        const parsedRows = result.data || [];
        if (parsedRows.length === 0) {
          toast.warning("CSV file is empty", optionToast);
          setCars([]);
          setColumns([]);
          return;
        }

        // Normalize keys and add placeholder gallery
        const normalized = parsedRows.map((row, index) => {
          const normalizedRow = {};
          Object.keys(row).forEach((key) => {
            const trimmedKey = key.trim();
            normalizedRow[trimmedKey] = row[key];
          });

          return {
            id: index,
            placeholder_image: placeholderImage,
            ...normalizedRow,
          };
        });

        const allKeys = new Set();
        normalized.forEach((row) => {
          Object.keys(row).forEach((k) => {
            if (k !== "id" && k !== "placeholder_image") {
              allKeys.add(k);
            }
          });
        });

        setCars(normalized);
        setColumns(Array.from(allKeys));
        toast.success("CSV loaded. Please verify and edit if needed.", optionToast);
      },
    });
  };

  const validateField = (field, value) => {
    if (value === null || value === undefined || String(value).trim() === "") {
      return true;
    }
    const v = String(value).trim();
    const f = field.toLowerCase();

    if (["year"].includes(f)) {
      if (!/^\d{4}$/.test(v)) return false;
      const yearNum = parseInt(v, 10);
      const currentYear = new Date().getFullYear();
      if (yearNum < 1900 || yearNum > currentYear) return false;
      return true;
    }

    if (["fixed_price", "price"].includes(f)) {
      return /^\d+(\.\d+)?$/.test(v);
    }

    if (["seating_capacity", "seats"].includes(f)) {
      return /^\d+$/.test(v);
    }

    if (["current_mileage", "mileage"].includes(f)) {
      return /^\d+$/.test(v);
    }

    if (["engine_capacity", "engine"].includes(f)) {
      return /^\d+$/.test(v);
    }


    return true;
  };

  const getFieldErrorMessage = (field) => {
    const f = field.toLowerCase();
    if (f === "year") return "Year must be a valid past or current year";
    if (["fixed_price", "price"].includes(f)) return "Price must be a valid number";
    if (["seating_capacity", "seats"].includes(f)) return "Seating capacity must be a number";
    if (["current_mileage", "mileage"].includes(f)) return "Mileage must be a number";
    if (["engine_capacity", "engine"].includes(f)) return "Engine capacity must be a number";
    return "Invalid value";
  };

  const handleCellChange = (rowId, field, value) => {
    const isValid = validateField(field, value);
    const key = `${rowId}-${field}`;

    setValidationErrors((prev) => {
      const next = { ...prev, [key]: !isValid };
      if (!isValid && !prev[key]) {
        toast.error(getFieldErrorMessage(field), optionToast);
      }
      if (isValid) {
        delete next[key];
      }
      return next;
    });

    setCars((prev) =>
      prev.map((car) =>
        car.id === rowId
          ? {
            ...car,
            [field]: value,
          }
          : car
      )
    );
  };

  const removeRow = (rowId) => {
    setCars((prev) => prev.filter((car) => car.id !== rowId));
  };

  const clearAll = () => {
    setCars([]);
    setColumns([]);
    setFileName("");
    if (fileInputRef && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImageChange = (rowId, event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);

    setCars((prev) =>
      prev.map((car) =>
        car.id === rowId
          ? {
            ...car,
            _localImageFile: file,
            _localImagePreview: previewUrl,
          }
          : car
      )
    );
  };

  const handleGalleryChange = (rowId, event) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (!files.length) return;

    setCars((prev) =>
      prev.map((car) =>
        car.id === rowId
          ? {
            ...car,
            _galleryFiles: files,
          }
          : car
      )
    );
  };

  const buildPayload = () => {
    return {
      owner_id: storedUserPreferences?.id,
      cars: cars.map((car) => ({
        registration_number: car.registration_number || car.Registration || "",
        fixed_price: car.fixed_price || car.price || "",
        make: car.make || car.Make || "",
        model: car.model || car.Model || "",
        model_varient:
          car.model_varient || car.model_variant || car["Model Variant"] || "",
        postcode: car.postcode || car.Postcode || "",
        year: car.year || car.Year || "",
        fuel_type: car.fuel_type || car["Fuel Type"] || "",
        body_type: car.body_type || car["Body Type"] || "",
        transmission_type:
          car.transmission_type || car["Transmission"] || car["Gearbox"] || "",
        color: car.color || car.Colour || car.Color || "",
        seating_capacity:
          car.seating_capacity || car["Seating Capacity"] || car.Seats || "",
        current_mileage:
          car.current_mileage || car.mileage || car.Mileage || "",
        engine_capacity:
          car.engine_capacity || car["Engine Capacity"] || car.Engine || "",
        specifications: car.specifications || car.description || "",
        phone: car.phone || storedUserPreferences?.phone || "",
        email: car.email || storedUserPreferences?.email || "",
        placeholder_image: car.placeholder_image || placeholderImage,
      })),
    };
  };

  const handleSave = async () => {
    if (!cars.length) {
      toast.warning("Please upload a CSV and verify data before saving.", optionToast);
      return;
    }
    const newErrors = {};
    cars.forEach((car) => {
      columns.forEach((col) => {
        const value = car[col];
        if (!validateField(col, value)) {
          newErrors[`${car.id}-${col}`] = true;
        }
      });
    });

    if (Object.keys(newErrors).length > 0) {
      setValidationErrors(newErrors);
      toast.error("Please fix validation errors before saving.", optionToast);
      return;
    }

    const result = await Swal.fire({
      title: "Save these cars as drafts?",
      html:
        "These cars will be saved as <b>unpublished</b>.<br/>" +
        "You must go to <b>My Inventory</b> later to publish them live.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00889C",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, save cars",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }
    const payload = buildPayload();

    const formData = new FormData();
    formData.append("owner_id", payload.owner_id || "");

    payload.cars.forEach((car, index) => {
      const prefix = `cars[${index}]`;

      Object.entries(car).forEach(([key, value]) => {
        if (key === "placeholder_image") return;
        if (value !== undefined && value !== null && value !== "") {
          formData.append(`${prefix}[${key}]`, value);
        }
      });

      const originalCar = cars[index];
      if (originalCar && originalCar._localImageFile) {
        formData.append(`${prefix}[previewImg]`, originalCar._localImageFile);
      }
      if (originalCar && Array.isArray(originalCar._galleryFiles)) {
        originalCar._galleryFiles.forEach((file) => {
          formData.append(`${prefix}[images][]`, file);
        });
      }
    });

    setIsSaving(true);
    try {
      const response = await axios.post(
        window.CARLOCA_API + "/dealer/cars/bulk-upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response?.data?.status) {
        const skipped = response?.data?.skipped || [];
        const created = response?.data?.created || [];

        const totalCreated = created.length;
        const totalSkipped = skipped.length;

        const skippedHtml =
          totalSkipped > 0
            ?
            `<div style="max-height:240px;overflow:auto;text-align:left;font-size:13px">` +
            `<p><b>Skipped entries (${totalSkipped}):</b></p>` +
            `<ul>` +
            skipped
              .map((item) => {
                const reg = item.registration_number || "(no reg)";
                const reason = item.reason || "Unknown reason";
                return `<li><b>${reg}</b> - ${reason}</li>`;
              })
              .join("") +
            `</ul>` +
            `</div>`
            : "";

        const summaryHtml =
          `<p><b>${totalCreated}</b> car(s) saved successfully.</p>` +
          (totalSkipped > 0
            ? `<p><b>${totalSkipped}</b> car(s) could not be saved.</p>`
            : "") +
          skippedHtml;

        await Swal.fire({
          title: totalSkipped > 0 ? "Bulk upload completed with some issues" : "Bulk upload completed",
          html: summaryHtml,
          icon: totalSkipped > 0 ? "info" : "success",
          confirmButtonColor: "#00889C",
          confirmButtonText: "OK",
          width: 600,
        });

        navigate("/dealer/my-inventory");
      } else {
        toast.error(response?.data?.message || "Upload failed", optionToast);
      }
    } catch (error) {
      console.error("Bulk upload error", error);
      toast.error("An error occurred while saving cars.", optionToast);
    } finally {
      setIsSaving(false);
    }
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileChange({ target: { files } });
    }
  };

  function backto() {
    window.history.back();
  }

  const columnsToHuman = (col) => {
    return col
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  return (
    <>
      <Navbar />
      <div className="cl-page bu-page-content">
        <div className="container-fluid px-3 px-md-5">
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
            <div>
              <h3 className="cl-page-title">Bulk Upload Cars</h3>
              <p className="cl-page-subtitle">Upload your inventory in seconds using CSV</p>
            </div>
            <div className="d-flex gap-2">
              <button onClick={backto} className="cl-btn cl-btn-outline cl-btn-sm">
                <i className="fa fa-arrow-left me-2"></i>Back
              </button>
            </div>
          </div>

          {!cars.length ? (
            /* STEP 1: UPLOAD */
            <div className="row justify-content-center">
              <div className="col-12 col-xl-12">
                <div className="cl-card p-4 p-md-5">
                  <div className="row g-4 align-items-center">
                    <div className="col-12 col-lg-7">
                      <div className="mb-4">
                        <span className="bu-step-badge">1</span>
                        <h5 className="d-inline-block fw-bold">Upload CSV file</h5>
                      </div>
                      <div
                        className={`bu-dropzone ${isDragging ? "dragging" : ""}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current.click()}
                      >
                        <i className="fa-solid fa-cloud-arrow-up bu-dropzone-icon"></i>
                        <h4 className="bu-dropzone-title">Drop your CSV here</h4>
                        <p className="bu-dropzone-text">or click to browse from computer</p>
                        <input
                          type="file"
                          accept=".csv"
                          ref={fileInputRef}
                          className="d-none"
                          onChange={handleFileChange}
                        />
                      </div>
                      <div className="mt-4 p-3 bg-light rounded-3">
                        <h6 className="small fw-bold mb-2">Instructions:</h6>
                        <ul className="small text-muted mb-0 ps-3">
                          <li>CSV must contain a header row with column names.</li>
                          <li>Required fields: registration_number, make, model, year, fuel_type.</li>
                          <li>Images can be added individually after uploading the file.</li>
                        </ul>
                      </div>
                    </div>
                    <div className="col-12 col-lg-5">
                      <div className="p-4 border border-dashed rounded-3 text-center bg-white">
                        <i className="fa-solid fa-file-csv fs-1 text-success mb-3"></i>
                        <h5>Need a Template?</h5>
                        <p className="small text-muted mb-4">
                          Download our sample CSV file to ensure your data format is correct.
                        </p>
                        <button
                          onClick={handleDownloadSampleCsv}
                          className="cl-btn cl-btn-primary w-100 py-2"
                        >
                          <i className="fa-solid fa-download me-2"></i>Download Sample CSV
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* STEP 2: VERIFY */
            <div className="row">
              <div className="col-12">
                <div className="mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <span className="bu-step-badge">2</span>
                    <h5 className="d-inline-block fw-bold mb-0">Verify & Refine Data</h5>
                  </div>
                  <div className="d-flex gap-2">
                    <button onClick={clearAll} className="cl-btn cl-btn-outline cl-btn-sm text-danger border-danger">
                      <i className="fa-solid fa-trash-can me-2"></i>Clear All
                    </button>
                  </div>
                </div>

                <div className="bu-table-container">
                  <div className="bu-table-scroll">
                    <table className="bu-table">
                      <thead>
                        <tr>
                          <th>Thumbnail</th>
                          <th>Gallery</th>
                          {columns.map((col) => (
                            <th key={col}>{columnsToHuman(col)}</th>
                          ))}
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cars.map((car) => (
                          <tr key={car.id}>
                            <td>
                              <div className="bu-img-preview-wrap">
                                <img
                                  src={car._localImagePreview || car.placeholder_image || placeholderImage}
                                  alt="Preview"
                                />
                                <label htmlFor={`car-image-${car.id}`} className="bu-img-overlay">
                                  <i className="fa-solid fa-camera"></i>
                                </label>
                                <input
                                  id={`car-image-${car.id}`}
                                  type="file"
                                  accept="image/*"
                                  className="d-none"
                                  onChange={(e) => handleImageChange(car.id, e)}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1">
                                <label
                                  htmlFor={`car-gallery-${car.id}`}
                                  className="cl-btn cl-btn-outline cl-btn-sm"
                                  style={{ padding: '0.2rem 0.5rem', fontSize: '10px' }}
                                >
                                  <i className="fa-solid fa-images me-1"></i> Gallery
                                </label>
                                <input
                                  id={`car-gallery-${car.id}`}
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  className="d-none"
                                  onChange={(e) => handleGalleryChange(car.id, e)}
                                />
                                {car._galleryFiles?.length > 0 && (
                                  <span className="badge text-white" style={{ fontSize: '9px', backgroundColor: '#00889C' }}>
                                    {car._galleryFiles.length} files
                                  </span>
                                )}
                              </div>
                            </td>
                            {columns.map((col) => (
                              <td key={col} style={{ minWidth: '150px' }}>
                                <input
                                  type="text"
                                  className={`bu-cell-input ${validationErrors[`${car.id}-${col}`] ? "bu-cell-error" : ""}`}
                                  value={car[col] || ""}
                                  onChange={(e) => handleCellChange(car.id, col, e.target.value)}
                                  placeholder={columnsToHuman(col)}
                                />
                              </td>
                            ))}
                            <td className="text-center">
                              <button
                                onClick={() => removeRow(car.id)}
                                className="btn btn-sm text-danger p-0"
                                title="Remove row"
                              >
                                <i className="fa-solid fa-circle-minus fs-5"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="bu-action-bar">
                    <span className="text-muted small">
                      <i className="fa-solid fa-info-circle me-1"></i>
                      {cars.length} cars successfully parsed.
                    </span>
                    <button
                      disabled={isSaving}
                      onClick={handleSave}
                      className="cl-btn cl-btn-primary px-5"
                    >
                      {isSaving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>Processing...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-cloud-arrow-up me-2"></i>Save All as Drafts
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
      <ToastContainer />
    </>
  );
};


export default BulkUploadCars;
